import React, { useState, useRef } from "react"
import { useStaticQuery, graphql, Link } from "gatsby"
import { Text, Box, Group, Collapse, Button } from "@mantine/core"
import { useDisclosure } from '@mantine/hooks'

// ========================================================
// 1. DESKTOP MENU ITEM (Custom + Dynamic Depth Direction!)
// ========================================================
const DesktopMenuItem = ({ item, level = 0 }) => {
    const hasChildren = item.children && item.children.length > 0;
    const activeStyles = { color: 'var(--mantine-color-blue-6)', backgroundColor: 'var(--mantine-color-blue-0)' }; 
    const isRealLink = item.path && item.path !== '#' && item.path !== '/#';
    
    // 💡 Η ΔΙΟΡΘΩΣΗ: Εδώ ελέγχουμε τι ιδιότητες θα πάρει το στοιχείο.
    // Αν είναι Link, παίρνει component, to, και το activeStyle. Αν δεν είναι, γίνεται απλό button (χωρίς activeStyle!)
    const linkProps = isRealLink 
        ? { component: Link, to: item.path, activeStyle: activeStyles } 
        : { component: 'button' };
    
    const isRoot = level === 0;
    const opensLeft = level >= 1;

    const [opened, setOpened] = useState(false);
    const timeoutRef = useRef();

    const openMenu = () => {
        clearTimeout(timeoutRef.current);
        setOpened(true);
    };

    const closeMenu = () => {
        timeoutRef.current = setTimeout(() => {
            setOpened(false);
        }, 120); 
    };

    // --- ΠΕΡΙΠΤΩΣΗ Α: Απλό Link (Τελικό κλαδί) ---
    if (!hasChildren) {
        return (
            <Button 
                {...linkProps} // 💡 Παίρνει τις ιδιότητες δυναμικά!
                variant="subtle" 
                color="gray"
                radius="sm"
                size={isRoot ? "md" : "sm"} 
                fw={isRoot ? 600 : 500} 
                justify="flex-start" 
                style={{ 
                    color: 'var(--mantine-color-dark-8)',
                    display: isRoot ? 'inline-flex' : 'flex', 
                    width: isRoot ? 'auto' : '100%', 
                }} 
                onFocus={openMenu}
                onBlur={closeMenu}
            >
                {item.label}
            </Button>
        );
    }

    // --- ΠΕΡΙΠΤΩΣΗ Β: Έχει παιδιά! ---
    return (
        <Box 
            onMouseEnter={openMenu}
            onMouseLeave={closeMenu}
            onFocusCapture={openMenu}
            onBlurCapture={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                    closeMenu();
                }
            }}
            style={{ 
                position: 'relative',
                display: isRoot ? 'inline-block' : 'block', 
                width: isRoot ? 'auto' : '100%', 
            }}
        >
            <Button 
                {...linkProps} // 💡 Παίρνει τις ιδιότητες δυναμικά!
                variant="subtle" 
                color="gray"
                radius="sm"
                size={isRoot ? "md" : "sm"} 
                fw={isRoot ? 600 : 500} 
                justify="space-between" 
                rightSection={<span style={{ fontSize: '10px', opacity: 0.5, marginLeft: '8px' }}>{isRoot ? '▼' : (opensLeft ? '◀' : '▶')}</span>}
                style={{ 
                    color: 'var(--mantine-color-dark-8)',
                    display: 'flex', 
                    width: '100%', 
                }} 
                onClick={(e) => {
                    if (!isRealLink) {
                        e.preventDefault();
                        setOpened(o => !o);
                    }
                }}
                onKeyDown={(e) => {
                    if (e.key === 'ArrowDown' && isRoot) { 
                        e.preventDefault(); 
                        openMenu(); 
                    }
                    if (e.key === 'Escape') { 
                        setOpened(false); 
                    }
                    if ((e.key === 'Enter' || e.key === ' ') && !isRealLink) { 
                        e.preventDefault(); 
                        setOpened((o) => !o); 
                    }
                }}
            >
                {item.label}
            </Button>

            {/* --- ΤΟ CUSTOM DROPDOWN ΥΠΟΜΕΝΟΥ --- */}
            <Box
                style={{
                    position: 'absolute',
                    top: isRoot ? '100%' : '0',
                    left: isRoot ? '0' : (opensLeft ? 'auto' : '100%'),
                    right: isRoot ? 'auto' : (opensLeft ? '100%' : 'auto'),
                    marginLeft: isRoot ? '0' : (opensLeft ? '0' : '2px'),
                    marginRight: isRoot ? '0' : (opensLeft ? '2px' : '0'),
                    marginTop: isRoot ? '4px' : '0',

                    minWidth: '220px',
                    backgroundColor: 'var(--mantine-color-body)',
                    boxShadow: 'var(--mantine-shadow-md)',
                    borderRadius: 'var(--mantine-radius-md)',
                    padding: '4px', 
                    zIndex: 1000,
                    border: '1px solid var(--mantine-color-gray-2)',
                    
                    opacity: opened ? 1 : 0,
                    visibility: opened ? 'visible' : 'hidden',
                    transform: opened ? 'translateY(0)' : 'translateY(-5px)',
                    transition: 'all 0.15s ease',
                    pointerEvents: opened ? 'auto' : 'none',
                }}
            >
                {isRealLink && (
                    <Button 
                        component={Link} 
                        to={item.path} 
                        variant="light" 
                        color="blue"
                        size="sm"
                        radius="sm"
                        fullWidth
                        fw={700}
                        justify="flex-start" 
                        style={{ marginBottom: '4px' }}
                        activeStyle={activeStyles} // <--- Εδώ είναι ασφαλές γιατί είμαστε σίγουροι ότι είναι Link!
                    >
                        Overview: {item.label}
                    </Button>
                )}
                
                {item.children.map((child) => <DesktopMenuItem key={child.id} item={child} level={level + 1} />)}
            </Box>
        </Box>
    );
};

// ========================================================
// 2. MOBILE MENU ITEM (Ανέπαφο)
// ========================================================
const MobileMenuItem = ({ item, closeMenu }) => { 
    const hasChildren = item.children && item.children.length > 0;
    const [opened, { toggle }] = useDisclosure(false);
    
    const activeStyles = { color: 'var(--mantine-color-blue-6)', backgroundColor: 'var(--mantine-color-blue-0)' }; 
    const isRealLink = item.path && item.path !== '#' && item.path !== '/#';
    const linkProps = isRealLink ? { component: Link, to: item.path, activeStyle: activeStyles } : {};

    if (!hasChildren) {
        return (
            <Button {...linkProps} variant="subtle" color="dark" size="lg" radius={0} fullWidth justify="flex-start" onClick={isRealLink ? closeMenu : undefined} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                <Text fw={500} style={{ color: 'inherit' }}>{item.label}</Text>
            </Button>
        );
    }

    return (
        <Box style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
            <Group wrap="nowrap" justify="space-between" align="stretch" w="100%" gap={0}>
                <Button {...linkProps} variant="subtle" color="dark" size="lg" radius={0} onClick={isRealLink ? closeMenu : toggle} style={{ flexGrow: 1 }} justify="flex-start">
                    <Text fw={600} style={{ color: 'inherit' }}>{item.label}</Text>
                </Button>
                <Button onClick={toggle} variant="subtle" color="dark" size="lg" radius={0} w={60} p={0} style={{ borderLeft: '1px solid var(--mantine-color-gray-2)' }} aria-label={opened ? `Κλείσιμο υπομενού ${item.label}` : `Άνοιγμα υπομενού ${item.label}`} aria-expanded={opened}>
                    <span style={{ transform: opened ? 'rotate(180deg)' : 'none', transition: 'transform 200ms ease' }}>▼</span>
                </Button>
            </Group>
            <Collapse in={opened}>
                <Box pl="lg" bg="var(--mantine-color-gray-0)">
                    {item.children.map(child => <MobileMenuItem key={child.id} item={child} closeMenu={closeMenu} />)}
                </Box>
            </Collapse>
        </Box>
    );
}

// ========================================================
// 3. MAIN NAV COMPONENT
// ========================================================
const MainNav = ({ closeMenu }) => {
    const { menu } = useStaticQuery(
        graphql`
        query GetMainNavigation {
            menu: allWpMenu(filter: {slug: {eq: "main-menu"}}) {
              nodes {
                menuItems {
                  nodes {
                    id
                    label
                    title
                    path
                    parentId
                  }
                }
              }
            }
          }
      `
    )

    const flatListToHierarchical = (data = [], { idKey = "id", parentKey = "parentId", childrenKey = "children" } = {}) => {
        const tree = []
        const childrenOf = {}
        data.forEach(item => {
            const newItem = { ...item }
            const { [idKey]: id, [parentKey]: parentId = 0 } = newItem
            childrenOf[id] = childrenOf[id] || []
            newItem[childrenKey] = childrenOf[id]
            parentId ? (childrenOf[parentId] = childrenOf[parentId] || []).push(newItem) : tree.push(newItem)
        })
        return tree
    }

    const menuItems = menu?.nodes[0]?.menuItems?.nodes ? menu.nodes[0].menuItems.nodes : []
    const hierarchicalList = flatListToHierarchical(menuItems)

    return (
        <>
            <Group visibleFrom="md" gap="sm">
                {hierarchicalList.map((item) => (
                    <DesktopMenuItem key={item.id} item={item} level={0} />
                ))}
            </Group>

            <Box hiddenFrom="md">
                {hierarchicalList.map((item) => (
                    <MobileMenuItem key={item.id} item={item} closeMenu={closeMenu} />
                ))}
            </Box>
        </>
    )
}

export default MainNav